/**
 * EventAccessControlPage.jsx — admin Event Access Control dashboard
 * ------------------------------------------------------------------
 * Mounted at `/dashboard/admin/access-control` (super_admin / org_admin
 * / event_manager).  Tabs: Passes · Visitor Registrations · Access Logs
 * · Zones & Gates · Analytics · Settings.  Built with the same look as
 * the Trainer Grading Queue / Goodwill Audit pages to stay visually
 * cohesive with the rest of the platform.
 */
import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import AccessPassCard from "../../components/AccessPassCard";
import {
  adminListPasses, adminListVisitorRegs, adminListLogs,
  adminGetAnalytics, adminListZones, adminCreateZone,
  adminPatchZone, adminGetSettings, adminPatchSettings,
  adminApprovePass, adminRevokePass, adminRejectPass,
  adminCreatePass, uploadPassPhoto, formatApiError,
  adminGetPricing, adminPatchPricing, adminRevokeWithRefund,
} from "../../lib/accessPassApi";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  IdCard, Users, ScrollText, MapPin, BarChart3, Settings as SettingsIcon,
  ShieldCheck, CheckCircle2, XCircle, RefreshCw, Plus, Loader2,
  ChevronRight, Inbox, AlertTriangle, ScanLine, Camera, X,
  IndianRupee, Receipt,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { value: "passes",     label: "Passes",       Icon: IdCard },
  { value: "visitors",   label: "Visitor Reg.", Icon: Users },
  { value: "logs",       label: "Access Logs",  Icon: ScrollText },
  { value: "zones",      label: "Zones & Gates",Icon: MapPin },
  { value: "pricing",    label: "Pricing",      Icon: IndianRupee },
  { value: "analytics",  label: "Analytics",    Icon: BarChart3 },
  { value: "settings",   label: "Settings",     Icon: SettingsIcon },
];

export default function EventAccessControlPage() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [tab, setTab] = useState("passes");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/events");
        const list = r.data?.data || r.data?.rows || r.data || [];
        setEvents(list);
        if (list.length && !eventId) setEventId(list[0].id);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-8 py-6 max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Diplomexa AccessPass
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-[#0A192F]">
              Event Access Control
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              QR passes · visitor registrations · zones · live entry logs.
            </p>
          </div>
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 max-w-xs"
            data-testid="access-control-event-select"
          >
            <option value="">— pick an event —</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.title}</option>
            ))}
          </select>
        </div>

        {/* Tab strip */}
        <div className="border-b border-slate-200 mb-4 flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.value
                  ? "border-[#D4AF37] text-[#0A192F]"
                  : "border-transparent text-slate-500 hover:text-[#0A192F]"
              }`}
              data-testid={`access-tab-${t.value}`}
            >
              <t.Icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {!eventId ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-12 text-center">
            <Inbox className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 font-semibold text-[#0A192F]">
              Pick an event to manage AccessPasses
            </p>
          </div>
        ) : (
          <>
            {tab === "passes"    && <PassesTab eventId={eventId} />}
            {tab === "visitors"  && <VisitorsTab eventId={eventId} />}
            {tab === "logs"      && <LogsTab eventId={eventId} />}
            {tab === "zones"     && <ZonesTab eventId={eventId} />}
            {tab === "pricing"   && <PricingTab eventId={eventId} />}
            {tab === "analytics" && <AnalyticsTab eventId={eventId} />}
            {tab === "settings"  && <SettingsTab eventId={eventId} />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// PASSES TAB
// ──────────────────────────────────────────────────────────────────────
function PassesTab({ eventId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);  // pass row pending revoke decision
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", role: "vip",
    organisation: "", access_level: "all_access", auto_approve: true,
  });
  const [createPhoto, setCreatePhoto] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminListPasses(eventId, status ? { status } : {});
      setRows(r.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [eventId, status]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (p) => {
    try { await adminApprovePass(p.id); toast.success("Pass approved + QR issued."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const handleRevoke = async (p) => {
    // Opens the Revoke modal so admin can choose refund yes/no.
    setRevokeTarget(p);
  };

  const performRevoke = async (refund) => {
    if (!revokeTarget) return;
    setRevokeBusy(true);
    try {
      const res = await adminRevokeWithRefund(revokeTarget.id, {
        pass_id: revokeTarget.id,
        reason: refund ? "Pass holder cancelled — refund issued" : "Manually revoked",
        refund,
      });
      if (refund) {
        toast.success(
          `Pass revoked + refund processed (${res.refund?.refund_id || "ok"}).`
        );
      } else {
        toast.success("Pass revoked.");
      }
      setRevokeTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevokeBusy(false);
    }
  };
  const handleReject = async (p) => {
    try { await adminRejectPass(p.id, "Rejected by admin"); toast.success("Pass rejected."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const handleCreate = async () => {
    try {
      const res = await adminCreatePass(eventId, { ...form, event_id: eventId });
      if (createPhoto) {
        try {
          await uploadPassPhoto(res.pass.id, createPhoto);
        } catch (perr) {
          toast.warning("Pass created but photo upload failed: " + formatApiError(perr));
        }
      }
      toast.success(`Pass for ${res.pass.full_name} issued.`);
      setCreateOpen(false);
      setForm({ full_name: "", email: "", phone: "", role: "vip", organisation: "", access_level: "all_access", auto_approve: true });
      setCreatePhoto(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border bg-white text-sm px-3 py-2"
            data-testid="passes-status-filter"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="revoked">Revoked</option>
          </select>
          <Button variant="outline" onClick={load} data-testid="passes-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
        <Button
          className="bg-[#0A192F] hover:bg-[#0F2C4F]"
          data-testid="issue-pass-btn"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" /> Issue VIP / staff pass
        </Button>
      </div>

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty label="No passes yet for this event." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Holder</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Issued</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.id}
                  className="border-t hover:bg-slate-50"
                  data-testid={`pass-row-${p.id}`}
                >
                  <td className="px-4 py-3">
                    <p className="font-semibold text-[#0A192F]">{p.full_name}</p>
                    <p className="text-xs text-slate-500">{p.email}</p>
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-[#0F4C81]">
                    {p.role || p.pass_type}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase font-bold tracking-wider rounded-full px-2 py-0.5 border ${
                      p.status === "active" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : p.status === "pending" ? "border-amber-300 bg-amber-50 text-amber-700"
                        : "border-rose-300 bg-rose-50 text-rose-700"
                    }`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {p.qr_issued_at ? new Date(p.qr_issued_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {p.approval_status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleApprove(p)}
                                    data-testid={`pass-approve-${p.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReject(p)}
                                    data-testid={`pass-reject-${p.id}`}>
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {p.status === "active" && (
                        <Button size="sm" variant="outline" onClick={() => handleRevoke(p)}
                                  data-testid={`pass-revoke-${p.id}`}>
                          Revoke
                        </Button>
                      )}
                      <Button size="sm" onClick={() => setActive(p)} className="bg-[#0A192F]"
                                data-testid={`pass-view-${p.id}`}>
                        <ScanLine className="h-3 w-3 mr-1" /> View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active && (
        <Modal onClose={() => setActive(null)}>
          <AccessPassCard pass={active} qrToken={null} />
          <p className="mt-3 text-xs text-amber-100 text-center">
            QR tokens are issued only to the pass holder via their dashboard.
          </p>
        </Modal>
      )}

      {revokeTarget && (
        <Modal onClose={() => !revokeBusy && setRevokeTarget(null)} narrow>
          <div className="rounded-2xl bg-white p-6 text-[#0A192F]">
            <div className="flex items-start gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <XCircle className="h-5 w-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-bold">Revoke this AccessPass?</h3>
                <p className="text-xs text-slate-500">
                  {revokeTarget.full_name} · {revokeTarget.role}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-700">
              Revoking immediately invalidates the QR code. The pass holder
              will no longer be able to enter the venue with this pass.
            </p>
            {revokeTarget.payment_status === "paid" && (
              <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 flex items-start gap-2">
                <Receipt className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  This is a <b>paid pass</b>. You can optionally refund the
                  payment now — refunds are processed through the original
                  gateway (Razorpay/Stripe) and routed via the existing
                  billing entity (Factskoot/Destineze).
                </span>
              </div>
            )}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setRevokeTarget(null)}
                disabled={revokeBusy}
                data-testid="revoke-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => performRevoke(false)}
                disabled={revokeBusy}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="revoke-only"
              >
                {revokeBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                Revoke only
              </Button>
              {revokeTarget.payment_status === "paid" && (
                <Button
                  onClick={() => performRevoke(true)}
                  disabled={revokeBusy}
                  className="bg-[#0A192F] hover:bg-[#0F2C4F]"
                  data-testid="revoke-with-refund"
                >
                  {revokeBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Receipt className="h-4 w-4 mr-2" />}
                  Revoke + refund
                </Button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {createOpen && (
        <Modal onClose={() => setCreateOpen(false)} narrow>
          <div className="rounded-2xl bg-white p-6 text-[#0A192F]">
            <h3 className="text-lg font-bold">Issue a VIP / staff AccessPass</h3>
            <p className="text-xs text-slate-500">Auto-approved + QR minted immediately.</p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <FieldI label="Full name" v={form.full_name} k="full_name" set={setForm} testid="create-pass-full-name" />
              <FieldI label="Email"     v={form.email}     k="email"     set={setForm} testid="create-pass-email" />
              <FieldI label="Phone"     v={form.phone}     k="phone"     set={setForm} testid="create-pass-phone" />
              <FieldI label="Org / company" v={form.organisation} k="organisation" set={setForm} testid="create-pass-org" />
              <label className="text-xs col-span-2">
                <span className="text-slate-600 font-semibold mb-1 block">Pass type</span>
                <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                        className="w-full rounded-lg border px-3 py-2 text-sm"
                        data-testid="create-pass-role">
                  {["vip","speaker","sponsor","press","staff","trainer","judge","partner","volunteer"].map((r) =>
                    <option key={r} value={r}>{r.toUpperCase()}</option>)}
                </select>
              </label>
              <label className="text-xs col-span-2">
                <span className="text-slate-600 font-semibold mb-1 block">Photo (optional, max 4 MB)</span>
                {createPhoto ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={URL.createObjectURL(createPhoto)}
                      alt="preview"
                      className="h-16 w-16 rounded-lg object-cover border-2 border-[#D4AF37]/60"
                      data-testid="create-pass-photo-preview"
                    />
                    <button type="button" onClick={() => setCreatePhoto(null)}
                              className="text-xs text-rose-600 underline"
                              data-testid="create-pass-photo-clear">
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600 hover:border-[#D4AF37]">
                    <Camera className="h-3.5 w-3.5 text-[#D4AF37]" />
                    Choose photo
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 4 * 1024 * 1024) { toast.error("Max 4 MB"); return; }
                        setCreatePhoto(f);
                      }}
                      className="hidden"
                      data-testid="create-pass-photo-input"
                    />
                  </label>
                )}
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="create-pass-cancel">Cancel</Button>
              <Button className="bg-[#0A192F] hover:bg-[#0F2C4F]" onClick={handleCreate} data-testid="create-pass-submit">
                <Plus className="h-4 w-4 mr-2" /> Issue pass
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function FieldI({ label, v, k, set, testid }) {
  return (
    <label className="text-xs">
      <span className="text-slate-600 font-semibold mb-1 block">{label}</span>
      <input value={v} onChange={(e) => set((f) => ({ ...f, [k]: e.target.value }))}
             className="w-full rounded-lg border px-3 py-2 text-sm" data-testid={testid} />
    </label>
  );
}

// ──────────────────────────────────────────────────────────────────────
// VISITOR REGISTRATIONS TAB
// ──────────────────────────────────────────────────────────────────────
function VisitorsTab({ eventId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await adminListVisitorRegs(eventId);
        setRows(r.rows || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally   { setLoading(false); }
    })();
  }, [eventId]);

  if (loading) return <Spinner />;
  if (!rows.length) return <Empty label="No visitor registrations yet." />;
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-4 py-2">Visitor</th>
            <th className="text-left px-4 py-2">Type</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Submitted</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v) => (
            <tr key={v.id} className="border-t" data-testid={`visitor-row-${v.id}`}>
              <td className="px-4 py-3">
                <p className="font-semibold">{v.full_name}</p>
                <p className="text-xs text-slate-500">{v.email}</p>
              </td>
              <td className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-[#0F4C81]">{v.pass_type}</td>
              <td className="px-4 py-3 text-xs">{v.status}</td>
              <td className="px-4 py-3 text-xs text-slate-500">
                {new Date(v.created_at).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// LOGS TAB
// ──────────────────────────────────────────────────────────────────────
function LogsTab({ eventId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminListLogs(eventId, result ? { result } : {});
      setRows(r.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally   { setLoading(false); }
  }, [eventId, result]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <select value={result} onChange={(e) => setResult(e.target.value)}
                className="rounded-lg border bg-white text-sm px-3 py-2"
                data-testid="logs-result-filter">
          <option value="">All results</option>
          <option value="approved">Approved</option>
          <option value="already_checked_in">Already in</option>
          <option value="wrong_zone">Wrong zone</option>
          <option value="wrong_event">Wrong event</option>
          <option value="revoked">Revoked</option>
          <option value="invalid">Invalid</option>
          <option value="expired">Expired</option>
        </select>
        <Button variant="outline" onClick={load} data-testid="logs-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>
      {loading ? <Spinner /> : rows.length === 0 ? (
        <Empty label="No scan logs yet — passes haven't been scanned." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Time</th>
                <th className="text-left px-4 py-2">Result</th>
                <th className="text-left px-4 py-2">Action</th>
                <th className="text-left px-4 py-2">Zone</th>
                <th className="text-left px-4 py-2">Scanner</th>
                <th className="text-left px-4 py-2">Pass</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`log-row-${r.id}`}>
                  <td className="px-4 py-2 text-xs">{new Date(r.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${
                      r.scan_result === "approved" ? "bg-emerald-50 text-emerald-700"
                      : "bg-rose-50 text-rose-700"
                    }`}>{r.scan_result}</span>
                    {r.duplicate_flag && (
                      <span className="ml-1 text-[10px] text-amber-700">DUP</span>
                    )}
                    {r.manual_override && (
                      <span className="ml-1 text-[10px] text-indigo-700">MANUAL</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{r.scan_action}</td>
                  <td className="px-4 py-2 text-xs">{r.zone_code || "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.scanned_by_name || r.scanner_role || "—"}</td>
                  <td className="px-4 py-2 text-xs font-mono">{(r.pass_id || "").slice(-10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// ZONES TAB
// ──────────────────────────────────────────────────────────────────────
function ZonesTab({ eventId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", code: "", allowed_pass_types: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await adminListZones(eventId); setRows(r.rows || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally  { setLoading(false); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      await adminCreateZone(eventId, {
        event_id: eventId, name: form.name, code: form.code.toUpperCase(),
        allowed_pass_types: form.allowed_pass_types
          ? form.allowed_pass_types.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean)
          : [],
        active: true,
      });
      setForm({ name: "", code: "", allowed_pass_types: "" });
      toast.success("Zone created.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (z) => {
    try { await adminPatchZone(z.id, { active: !z.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-4">
      <div>
        {loading ? <Spinner /> : rows.length === 0 ? (
          <Empty label="No zones configured. Add one →" />
        ) : (
          <div className="rounded-xl border bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Allowed types</th>
                  <th className="text-left px-4 py-2">Active</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((z) => (
                  <tr key={z.id} className="border-t" data-testid={`zone-row-${z.id}`}>
                    <td className="px-4 py-3 font-semibold">{z.name}</td>
                    <td className="px-4 py-3 text-xs uppercase tracking-wider font-mono">{z.code}</td>
                    <td className="px-4 py-3 text-xs">
                      {z.allowed_pass_types?.length ? z.allowed_pass_types.join(", ") : "All"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggle(z)}
                        data-testid={`zone-toggle-${z.id}`}
                        className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 border ${
                          z.active ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : "border-slate-300 bg-slate-50 text-slate-500"
                        }`}
                      >
                        {z.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="rounded-xl border bg-white p-4">
        <p className="text-sm font-bold text-[#0A192F]">Add zone</p>
        <p className="text-xs text-slate-500">e.g. VIP, Delegate Hall, Lunch Area</p>
        <div className="mt-3 space-y-3">
          <FieldI label="Name" v={form.name} k="name" set={setForm} testid="zone-input-name" />
          <FieldI label="Code (short, uppercase)" v={form.code} k="code" set={setForm} testid="zone-input-code" />
          <FieldI label="Allowed types (comma-separated, blank = all)" v={form.allowed_pass_types}
                  k="allowed_pass_types" set={setForm} testid="zone-input-types" />
          <Button onClick={handleCreate} disabled={!form.name || !form.code}
                  className="w-full bg-[#0A192F] hover:bg-[#0F2C4F]" data-testid="zone-create-btn">
            <Plus className="h-4 w-4 mr-2" /> Create zone
          </Button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// ANALYTICS TAB
// ──────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────
// PRICING TAB — Chunk B (paid visitor passes)
// ──────────────────────────────────────────────────────────────────────
const PRICEABLE_TYPES = [
  { key: "visitor",  label: "Visitor" },
  { key: "observer", label: "Observer" },
  { key: "parent",   label: "Parent / Guardian" },
  { key: "guest",    label: "Guest" },
  { key: "speaker",  label: "Speaker" },
  { key: "sponsor",  label: "Sponsor" },
  { key: "volunteer",label: "Volunteer" },
  { key: "vip",      label: "VIP / Chief Guest" },
  { key: "press",    label: "Press / Media" },
];

function PricingTab({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState({});
  const [enabled, setEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminGetPricing(eventId);
      setData(d);
      setEnabled(!!d.paid_passes_enabled);
      const initial = {};
      for (const t of PRICEABLE_TYPES) {
        initial[t.key] = d.prices?.[t.key] || {
          enabled: false, amount_inr: 0, amount_usd: 0,
          currency_default: "INR", active: true,
        };
      }
      setRows(initial);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const saveAll = async () => {
    setSaving(true);
    try {
      await adminPatchPricing(eventId, {
        paid_passes_enabled: enabled,
        prices: rows,
      });
      toast.success("Pricing saved.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (loading) return <Spinner />;
  if (!data) return <Empty label="Could not load pricing." />;

  const update = (k, field, v) =>
    setRows((r) => ({ ...r, [k]: { ...r[k], [field]: v } }));

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <p className="text-sm font-bold text-[#0A192F]">
            Paid AccessPass pricing
          </p>
          <p className="text-xs text-slate-500 max-w-2xl">
            Pick which pass categories the public has to pay for at this event.
            INR pricing is <b>inclusive of 18 % GST</b> · routed through
            Factskoot Eduwellness via Razorpay. Foreign-currency pricing is
            routed through Destineze via Stripe.
          </p>
          <div className="mt-2 flex gap-2 flex-wrap text-[10px] uppercase tracking-wider font-bold">
            <span className={`rounded-full px-2 py-0.5 border ${
              data.razorpay_configured
                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                : "bg-rose-50 text-rose-700 border-rose-300"
            }`} data-testid="pricing-razorpay-status">
              Razorpay {data.razorpay_configured ? "configured" : "missing"}
            </span>
            <span className={`rounded-full px-2 py-0.5 border ${
              data.stripe_configured
                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                : "bg-amber-50 text-amber-700 border-amber-300"
            }`} data-testid="pricing-stripe-status">
              Stripe {data.stripe_configured ? "configured" : "not configured"}
            </span>
            <span className="rounded-full px-2 py-0.5 border bg-slate-50 text-slate-600 border-slate-300"
                    data-testid="pricing-mode">
              Mode: {data.payment_mode}
            </span>
            <span className="rounded-full px-2 py-0.5 border bg-slate-50 text-slate-600 border-slate-300">
              GST: {data.gst_treatment}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="pricing-master-toggle"
            />
            <span className="font-semibold text-[#0A192F]">
              Enable paid passes for this event
            </span>
          </label>
          <Button
            onClick={saveAll}
            disabled={saving}
            className="bg-[#0A192F] hover:bg-[#0F2C4F]"
            data-testid="pricing-save"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Save pricing
          </Button>
        </div>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Pass type</th>
              <th className="text-center px-4 py-2">Paid?</th>
              <th className="text-right px-4 py-2">INR (incl. GST)</th>
              <th className="text-right px-4 py-2">USD</th>
              <th className="text-center px-4 py-2">Default currency</th>
              <th className="text-center px-4 py-2">Active</th>
            </tr>
          </thead>
          <tbody>
            {PRICEABLE_TYPES.map((t) => {
              const r = rows[t.key] || {};
              return (
                <tr key={t.key} className="border-t" data-testid={`pricing-row-${t.key}`}>
                  <td className="px-4 py-3 font-semibold">{t.label}</td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={!!r.enabled}
                      onChange={(e) => update(t.key, "enabled", e.target.checked)}
                      data-testid={`pricing-enabled-${t.key}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      disabled={!r.enabled}
                      value={r.amount_inr || 0}
                      onChange={(e) => update(t.key, "amount_inr", Number(e.target.value) || 0)}
                      className="w-24 rounded border px-2 py-1 text-right disabled:opacity-50"
                      data-testid={`pricing-amount-inr-${t.key}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      disabled={!r.enabled}
                      value={r.amount_usd || 0}
                      onChange={(e) => update(t.key, "amount_usd", Number(e.target.value) || 0)}
                      className="w-20 rounded border px-2 py-1 text-right disabled:opacity-50"
                      data-testid={`pricing-amount-usd-${t.key}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <select
                      disabled={!r.enabled}
                      value={r.currency_default || "INR"}
                      onChange={(e) => update(t.key, "currency_default", e.target.value)}
                      className="rounded border px-2 py-1 text-sm disabled:opacity-50"
                      data-testid={`pricing-currency-${t.key}`}
                    >
                      {["INR", "USD", "EUR", "GBP", "AED", "HKD"].map((c) =>
                        <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={r.active !== false}
                      onChange={(e) => update(t.key, "active", e.target.checked)}
                      data-testid={`pricing-active-${t.key}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[11px] text-slate-500 max-w-3xl">
        Paid pass payment is confirmed by the gateway webhook (Razorpay /
        Stripe) — until then the QR is dormant and the scanner shows
        &quot;Payment Pending&quot;. INR payments are receipted by Factskoot
        Eduwellness Pvt. Ltd. with 18 % GST. Foreign-currency payments are
        receipted by Destineze.
      </p>
    </div>
  );
}


/**
 * AnalyticsStat — hoisted module-scope tile so React doesn't unmount +
 * remount the entire subtree on every AnalyticsTab re-render.
 */
function AnalyticsStat({ label, value, tone = "navy", testid }) {
  return (
    <div
      className={`rounded-sm p-4 border ${
        tone === "gold"
          ? "border-brand-gold/50 bg-brand-gold/5"
          : "border-slate-200 bg-white"
      }`}
      data-testid={testid}
    >
      <p className="eyebrow text-slate-500">{label}</p>
      <p className="mt-2 font-display text-2xl text-brand-navy-dark">{value}</p>
    </div>
  );
}


function AnalyticsTab({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminGetAnalytics(eventId)); }
    catch (e) { toast.error(formatApiError(e)); }
    finally  { setLoading(false); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <Spinner />;
  if (!data) return <Empty label="No analytics yet." />;

  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <AnalyticsStat label="Total passes"     value={data.total_passes}     testid="stat-total-passes" />
        <AnalyticsStat label="Active passes"    value={data.active_passes}    testid="stat-active-passes" tone="gold" />
        <AnalyticsStat label="Checked in"       value={data.checked_in}       testid="stat-checked-in" />
        <AnalyticsStat label="Not arrived"      value={data.not_arrived}      testid="stat-not-arrived" />
        <AnalyticsStat label="Pending approval" value={data.pending_approval} testid="stat-pending" />
        <AnalyticsStat label="Revoked"          value={data.revoked}          testid="stat-revoked" />
        <AnalyticsStat label="Denied scans"     value={data.denied_scans}     testid="stat-denied" />
        <AnalyticsStat label="Duplicate scans"  value={data.duplicate_scans}  testid="stat-duplicates" />
      </div>
      <div className="mt-5 grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-sm font-bold text-[#0A192F] mb-2">Scans by result</p>
          {Object.keys(data.scans_by_result || {}).length === 0 ? (
            <p className="text-xs text-slate-500">No scans yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {Object.entries(data.scans_by_result).map(([k, v]) => (
                <li key={k} className="flex justify-between border-b py-1">
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="font-bold">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-sm font-bold text-[#0A192F] mb-2">Passes by type</p>
          {Object.keys(data.passes_by_type || {}).length === 0 ? (
            <p className="text-xs text-slate-500">No passes yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {Object.entries(data.passes_by_type).map(([k, v]) => (
                <li key={k} className="flex justify-between border-b py-1">
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="font-bold">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// SETTINGS TAB
// ──────────────────────────────────────────────────────────────────────
function SettingsTab({ eventId }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try { setSettings(await adminGetSettings(eventId)); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [eventId]);
  if (!settings) return <Spinner />;

  const save = async (patch) => {
    setSaving(true);
    try {
      const s = await adminPatchSettings(eventId, patch);
      setSettings(s);
      toast.success("Settings saved.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally     { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border bg-white p-6 max-w-3xl">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!settings.visitor_registration_enabled}
          onChange={(e) => save({ visitor_registration_enabled: e.target.checked })}
          data-testid="settings-visitor-reg-toggle"
        />
        <div>
          <p className="font-semibold text-[#0A192F]">Visitor registration enabled</p>
          <p className="text-xs text-slate-500">
            Exposes <span className="font-mono">/events/{`{slug}`}/visitor-register</span> publicly.
          </p>
        </div>
      </label>

      <div className="mt-5">
        <p className="text-xs font-semibold text-slate-600 mb-1">Instant-approval pass types (comma-separated)</p>
        <input
          defaultValue={(settings.instant_approval_pass_types || []).join(", ")}
          onBlur={(e) =>
            save({ instant_approval_pass_types:
                e.target.value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean) })}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          data-testid="settings-instant-approval-input"
        />
      </div>
      <div className="mt-5">
        <p className="text-xs font-semibold text-slate-600 mb-1">Welcome message</p>
        <textarea
          rows={2}
          defaultValue={settings.welcome_message || ""}
          onBlur={(e) => save({ welcome_message: e.target.value })}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          data-testid="settings-welcome-msg"
        />
      </div>
      <div className="mt-5">
        <p className="text-xs font-semibold text-slate-600 mb-1">Venue instructions</p>
        <textarea
          rows={3}
          defaultValue={settings.venue_instructions || ""}
          onBlur={(e) => save({ venue_instructions: e.target.value })}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          data-testid="settings-venue-instructions"
        />
      </div>
      <div className="mt-5">
        <p className="text-xs font-semibold text-slate-600 mb-1">Terms text (shown to visitors)</p>
        <textarea
          rows={3}
          defaultValue={settings.terms_text || ""}
          onBlur={(e) => save({ terms_text: e.target.value })}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          data-testid="settings-terms"
        />
      </div>
      {saving && (
        <p className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Reusable little widgets
// ──────────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" />
      Loading…
    </div>
  );
}
function Empty({ label }) {
  return (
    <div className="text-center py-12 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60">
      <Inbox className="h-8 w-8 text-slate-300 mx-auto" />
      <p className="mt-2 text-sm text-slate-500">{label}</p>
    </div>
  );
}
function Modal({ children, onClose, narrow = false }) {
  return (
    <div
      className="fixed inset-0 z-40 bg-[#0A192F]/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className={`w-full ${narrow ? "max-w-md" : "max-w-xl"}`} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
